import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Link,
  IconButton,
  Chip,
  debounce,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import to from "await-to-js";
import Toaster from "../components/Toaster";
import Grid from "@mui/material/Grid2";
import saveAs from "file-saver";
import * as XLSX from "xlsx";
import { axiosWithAuth } from "../plugins/axios";
import CloseIcon from "@mui/icons-material/Close";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridSortModel,
  GridColumnMenuContainer,
  GridColumnMenuProps,
  GridColumnMenuSortItem,
  GridColumnMenuHideItem,
  GridColumnMenuManageItem,
  GridValueGetter,
  getGridStringOperators,
  GridFilterModel,
} from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";

interface Cohort {
  _id: number;
  cohortName: string;
  status: string; // Add this field to track the evaluation status
  studentId: string;
  userDetails: {
    name: string;
  };
  aiEvaluatedRubrics?: {
    deviation?: string;
  };
  submissionId: GridRowId;
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  const { hideMenu, colDef, open } = props;
  return (
    <GridColumnMenuContainer hideMenu={hideMenu} colDef={colDef} open={open}>
      <GridColumnMenuSortItem colDef={colDef} onClick={hideMenu} />
      <GridColumnMenuHideItem colDef={colDef} onClick={hideMenu} />
      <GridColumnMenuManageItem colDef={colDef} onClick={hideMenu} />
    </GridColumnMenuContainer>
  );
}
function DashboardEvaluations() {
  const [evaluations, setEvaluations] = useState<Cohort[]>([]);
  const [cohortDetails, setCohortDetails] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [isRunning, setIsRunning] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [numberOfSubmissions, setNumberOfSubmissions] =
    React.useState<any>(totalCount);
  const [selectedIDs, setSelectedIDs] = useState<readonly GridRowId[]>([]);
  const [checkBoxDisabled, setCheckBoxDisabled] = useState(false);
  const [isRowSelectable_, setIsRowSelectable] = useState(true);
  const handleChange = (event: SelectChangeEvent) => {
    setSelectedIDs([]);
    setNumberOfSubmissions(event.target.value);
    setIsRowSelectable(false);
  };
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  console.log("selectedIDs: ", selectedIDs);
  const { courseId, quizId } = useParams();

  const apiUrl = process.env.REACT_APP_PIPELINE_API;

  const navigate = useNavigate();

  const debounceFetchEvaluations = useMemo(
    () =>
      debounce((filter) => {
        fetchEvaluations(filter);
      }, 1000),
    [sortModel]
  );

  const nestedColumnValues: { [key: string]: string } = {
    userDetails: "userDetails.name",
    aiEvaluatedRubrics: "aiEvaluatedRubrics.deviation",
    userProgram: "userProgram.workshop.code",
    userProgram1: "userProgram.program.name",
  };

  const handleFilterChange = ({ items }: GridFilterModel) => {
    const { field, value, operator } = items[0] || {};
    const filter = {
      filterColumn: nestedColumnValues[field]
        ? nestedColumnValues[field]
        : field,
      filterValue: value,
      filterOperator: operator,
    };
    debounceFetchEvaluations(filter);
  };

  const fetchEvaluations = async (filter?: any) => {
    setLoading(true);
    const [err, response] = (await to(
      axiosWithAuth(
        `${apiUrl}autograde/evaluations/${courseId}/${quizId}`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            sortColumn: sortModel[0]?.field || null,
            sortDirection: sortModel[0]?.sort || null,
            evaluatedFor: { $ne: "testing" },
            ...filter,
          },
        },

        "get"
      )
    )) as any;

    if (err) {
      setError("Failed to load evaluations");
      setLoading(false);
      return;
    }

    const { data, totalCount } = response!.data;
    console.log("total count: ", totalCount);
    setEvaluations(data);
    setTotalCount(totalCount);
    setNumberOfSubmissions(totalCount);
    setLoading(false);

    // Check if any evaluation is still running
    const runningEvaluation = data.some(
      (evalItem: Cohort) => evalItem.status === "pending"
    );
    setIsRunning(runningEvaluation);
  };

  useEffect(() => {
    return () => debounceFetchEvaluations.clear();
  }, [debounceFetchEvaluations]);
  const fetchCourseDetails = async () => {
    const [err, response] = (await to(
      axiosWithAuth(
        `${apiUrl}cohortConfiguration/cohort/${courseId}/quiz/${quizId}`,
        {},
        "get"
      )
    )) as any;

    if (err) {
      setError("Failed to load course details");
      setLoading(false);
      return;
    }

    setCohortDetails(response!.data);
  };

  useEffect(() => {
    fetchCourseDetails();
    fetchEvaluations();
  }, [apiUrl, courseId, quizId, page, rowsPerPage, sortModel]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const runEvaluations = async () => {
  //   setIsRowSelectable(false);
  //   setIsRunning(true);
  //   if (evaluations.length > 0) {
  //     let payload = {} as any;

  //     if (selectedIDs && selectedIDs.length > 0) {
  //       payload.evaluationIds = selectedIDs;
  //     } else if (numberOfSubmissions !== null) {
  //       payload.numberOfSubmissions = numberOfSubmissions;
  //     }
  //     const [err] = (await to(
  //       axiosWithAuth(
  //         `${apiUrl}autograde/rerun/evaluations/${courseId}/${quizId}`,
  //         payload,
  //         "post"
  //       )
  //     )) as any;
  //     // const [err, response] = await to(axios.post(`${apiUrl}autograde/rerun/evaluations/${cohortId}/${quizId}`, {})) as any;
  //     if (err) {
  //       const { data: { message = "Error deleting evaluations" } = {} } =
  //         err.response || {};
  //       setToastMessage(message);
  //       setToastOpen(true);
  //       setIsRunning(false);
  //       return;
  //     }
  //   } else {
  //     const [runError] = (await to(
  //       axiosWithAuth(
  //         `${apiUrl}autograde/${courseId}/${quizId}`,
  //         { numberOfSubmissions },
  //         "post"
  //       )
  //     )) as any;
  //     // const [runError, response] = await to(axios.post(`${apiUrl}autograde/${cohortId}/${quizId}`, {})) as any;

  //     if (runError) {
  //       const { data: { message = "Error running evaluations" } = {} } =
  //         runError.response || {};
  //       setToastMessage(message);
  //       setToastOpen(true);
  //       setIsRunning(false);
  //       return;
  //     }
  //   }
  //   // setToastMessage("Evaluations started successfully");
  //   // setToastOpen(true);
  //   setIsRunning(true);
  //   fetchEvaluations();
  // };

  // const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files) {
  //     setFile(e.target.files[0]);
  //   }
  // };

  // const handleFormSubmit = async () => {
  //   if (!file) return;

  //   setFormLoading(true);
  //   const formData = new FormData();
  //   formData.append("file", file);

  //   // First, upload the file to get the URL
  //   const [uploadError, uploadResponse] = await to(
  //     axiosWithAuth(`${apiUrl}autograde/upload`, formData, "post", {
  //       headers: {
  //         "Content-Type": "multipart/form-data",
  //       },
  //     })
  //   );

  //   if (uploadError) {
  //     setToastMessage("Failed to upload file");
  //     setToastOpen(true);
  //     setFormLoading(false);
  //     return;
  //   }

  //   const fileUrl = uploadResponse?.data.url;

  //   const [urlError, urlResponse] = (await to(
  //     axiosWithAuth(
  //       `${apiUrl}autograde/manualgrade/${courseId}/${quizId}`,
  //       { fileUrl, courseId, quizId },
  //       "post"
  //     )
  //   )) as any;

  //   // const [urlError, urlResponse] = await to(axios.post(`${apiUrl}autograde/manualgrade/${cohortId}/${quizId}`, { fileUrl, cohortId, quizId }));

  //   if (urlError) {
  //     setToastMessage("Failed to submit file URL");
  //     setToastOpen(true);
  //     setFormLoading(false);
  //     return;
  //   }

  //   setModalOpen(false);
  //   setFile(null);
  //   setFormLoading(false);
  //   fetchEvaluations();
  // };

  const flattenData = async (data: any) => {
    const result = [] as any;

    data?.forEach((l1: any) => {
      const l1Name = l1.name;
      const comment = l1.comment;

      l1.children?.forEach((l2: any) => {
        const l2Name = l2.name;

        l2.children?.forEach((l3: any) => {
          result.push({
            ...l3,
            l3Name: l3.name,
            l2Name,
            l1Name,
            comment,
          });
        });
      });
    });
    return result;
  };

  const downloadReports = async () => {
    const workbook = XLSX.utils.book_new(); // Create a new workbook
    let index = 0;

    const filteredEvaluations =
      selectedIDs && selectedIDs.length > 0
        ? evaluations.filter((evaluation) =>
          selectedIDs.includes(evaluation._id)
        )
        : evaluations;

    // setEvaluations(filteredEvaluations)

    if (filteredEvaluations.length === 0) {
      setToastMessage(
        "No evaluations found to download reports. Please select evaluations or ensure data exists."
      );
      setToastOpen(true);
      return;
    }

    for (const evaluation of filteredEvaluations) {
      const {
        humanEvaluatedRubrics: humanEvaluatedRubricsData = {},
        aiEvaluatedRubrics: aiEvaluatedRubricsData = {},
        failureReason = null,
      } = (evaluation as any) || {};

      const { children: humanEvaluatedRubrics = [] } = humanEvaluatedRubricsData || {}

      const aiEvaluatedRubrics = aiEvaluatedRubricsData?.children || [];

      let evaluationData;

      if (humanEvaluatedRubrics.length > 0) {
        const [humanErr, humanFlattedData] = await to(
          flattenData(humanEvaluatedRubrics)
        );

        const [aiErr, l3Data] = await to(flattenData(aiEvaluatedRubrics));

        if (humanErr || aiErr) {
          setError("Failed to process evaluation data");
          setLoading(false);
          return;
        }

        evaluationData = l3Data.map((l3: any) => {
          const humanFeedback = humanFlattedData.find(
            (h: any) => h.id === l3.id
          );
          return {
            ...l3,
            humanFeedback: humanFeedback ? humanFeedback.comment || humanFeedback.feedback : "",
          };
        });

        if (failureReason && aiEvaluatedRubrics.length === 0) {
          evaluationData = [{ failureReason: failureReason }];
        }
      } else {
        const [aiErr, l3Data] = await to(flattenData(aiEvaluatedRubrics));
        if (aiErr) {
          setError("Failed to process evaluation data");
          setLoading(false);
          return;
        }
        evaluationData = l3Data;
      }

      if (failureReason && aiEvaluatedRubrics.length === 0) {
        evaluationData = [{ failureReason: failureReason }];
      }

      const worksheet = XLSX.utils.json_to_sheet(evaluationData);

      const sheetName = evaluation?.userDetails?.name
        ? `${evaluation.studentId}_${evaluation.userDetails.name}`
        : `${evaluation.submissionId}_manual_upload`;
      const truncatedSheetName =
        sheetName.length > 31 ? `${sheetName.slice(0, 28)}...` : sheetName;

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        truncatedSheetName
        // `Eval ${index+1} ${evaluation._id}`
      );

      index += 1;
    }

    const workbookBinary = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "binary",
    });

    const blob = new Blob([s2ab(workbookBinary)], {
      type: "application/octet-stream",
    });

    saveAs(blob, `cohort_${courseId}_quiz_${quizId}_all_evaluations.xlsx`);
  };

  const s2ab = (s: string) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
      view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
  };

  // if (loading) {
  //   return (
  //     <Box mt={4} display="flex" justifyContent="center">
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const downloadAsTextFile = (data: any, filename: any) => {
    const blob = new Blob([data], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCustomFilterOperators = () =>
    getGridStringOperators().filter(
      (operator) => operator.value === "contains" || operator.value === "equals"
    );

  const calculateDeviation = (aiScore: number | null, humanScore: number | null) => {
    console.log(aiScore, humanScore);
    if (aiScore === null || humanScore === null) {
      return '-';
    } else if (aiScore === 0 && humanScore === 0) {
      return '0%';
    } else if (aiScore === 0) {
      return '-100%';
    } else if (humanScore === 0) {
      return '100%';
    } else {
      const deviation = ((aiScore - humanScore) * 100) / aiScore;
      return `${deviation.toFixed(2)}%`;
    }
  }

  const columns: GridColDef[] = [
    {
      field: "submissionId",
      headerName: "Evaluation Id",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          href={`/performanceDashboard/listOfQuizzes/cohort/${params.row?.courseId}/quiz/${params.row?.quizId}/evaluation/${params.row?._id}/evaluation-details`}
          // target="_blank"
          // rel="noopener noreferrer"
          sx={{ textDecoration: "none" }}
        >
          {params.row?.submissionId}
        </Link>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "studentId",
      headerName: "Student Id",
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.row?.studentId === "1" ? "N/A" : params.row?.studentId,
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "userProgram",
      headerName: "Cohort",
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.row?.userProgram?.workshop?.code,
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "userProgram1",
      headerName: "Program",
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.row?.userProgram?.program?.name,
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "userDetails",
      headerName: "Student Name",
      width: 150,
      // renderCell: (params: GridRenderCellParams) =>
      //   params.row.userDetails?.name || "N/A",
      valueGetter: (params: GridValueGetter) => {
        return params?.name || "-";
      },
      filterOperators: getCustomFilterOperators(),
      renderCell: (params: GridRenderCellParams) => params.row?.userDetails?.name || "N/A",
    },
    {
      field: "cdnLink",
      headerName: "Submission File",
      width: 150,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          aria-label="delete"
          href={params.row?.cdnLink}
          target="_blank"
          sx={{
            color: "black",
            transition: "transform 0.2s, color 0.2s",
            ":hover": {
              transform: "scale(1.2)",
              color: "primary.main",
            },
          }}
        >
          <DownloadIcon />
        </IconButton>
      ),
    },
    {
      field: "parsedData",
      headerName: "Parsed Data",
      align: "center",
      headerAlign: "center",
      width: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const isPending = params.row?.status === "pending";

        return (
          <IconButton
            sx={{
              color: "black",
              transition: "transform 0.2s, color 0.2s",
              ":hover": {
                transform: "scale(1.2)",
                color: "primary.main",
              },
            }}
            disabled={isPending}
          >
            <DownloadIcon
              onClick={() => {
                const data = params.row?.parsedData;
                const studentId = params.row?.studentId || "manual_upload";
                const userName = params.row?.userDetails?.name;
                const filename = userName
                  ? `${studentId}_${userName}.txt`
                  : `manual_upload_${Math.floor(
                    100 + Math.random() * 900
                  )}.txt`;
                downloadAsTextFile(data, filename);
              }}
            />
          </IconButton>
        );
      },
    },

    {
      field: "updatedAt",
      headerName: "Last Modified On",
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.row?.updatedAt
          ? new Date(params.row?.updatedAt).toLocaleString()
          : "-",
      valueGetter: (params: GridValueGetter) => {
        return new Date(params as unknown as string).toLocaleString();
      },
      filterable: false,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value == "contains" || operator.value == "equals"
      ),
    },
    {
      field: "maxMarks",
      headerName: "Max Marks",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => params.row?.maxMarks || "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "humanEvaluatedMarks",
      headerName: "Human Evaluated Marks",
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams) =>
        params.row?.humanEvaluatedMarks !== undefined && params.row?.humanEvaluatedMarks !== null
          ? params.row?.humanEvaluatedMarks
          : "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "aiEvaluatedMarks",
      headerName: "AI Evaluated Marks",
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams) =>
        params.row?.aiEvaluatedMarks !== undefined && params.row?.aiEvaluatedMarks !== null
          ? params.row?.aiEvaluatedMarks
          : "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "aiEvaluatedRubrics",
      headerName: "Deviation",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const humanEvaluatedMarks = params.row?.humanEvaluatedMarks ?? null;
        const aiEvaluatedMarks = params.row?.aiEvaluatedMarks ?? null;
        // if ((humanEvaluatedMarks === null || humanEvaluatedMarks === 0) &&
        //   (aiEvaluatedMarks === null || aiEvaluatedMarks === 0)) {
        //   return "0%";
        // }

        let val: string | undefined = calculateDeviation(aiEvaluatedMarks, humanEvaluatedMarks);
        if (val && val !== "-") {
          val = val.slice(0, -1);
          val = `${Math.round(Number(val))}%`;
        }

        return val || "-";
      },
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const status = params.row?.status;
        const color =
          status === "completed"
            ? "success"
            : status === "failed"
              ? "error"
              : "default";

        return (
          <Chip
            label={status}
            variant="outlined"
            color={color}
            style={{
              width: "150px",
              borderRadius: "16px",
              textAlign: "center",
            }}
          />
        );
      },
      filterOperators: getCustomFilterOperators(),
    },
  ];

  return (
    <Box mt={4}>
      <Box pb={4}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography gutterBottom>
              Cohort:{" "}
              <Typography display={"inline"} fontWeight={"bold"}>
                {courseId}
              </Typography>
            </Typography>
            <Typography fontWeight={"bold"}>
              {cohortDetails.cohortName}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography gutterBottom>
              Quiz:{" "}
              <Typography display={"inline"} fontWeight={"bold"}>
                {quizId}
              </Typography>
            </Typography>
            <Typography gutterBottom fontWeight={"bold"}>
              {cohortDetails.quizName}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Typography variant="h4" gutterBottom>
        List of Evaluations
      </Typography>
      <Box justifyContent={"right"} display={"flex"}>
        <Button
          variant="outlined"
          onClick={downloadReports}
          sx={{ mr: 3 }}
          disabled={totalCount <= 0}
        >
          {selectedIDs.length === 0
            ? "Download all reports"
            : selectedIDs.length === totalCount
              ? "Download all reports"
              : "Download selected reports"}
        </Button>
        {/* <Button
          variant="outlined"
          onClick={() => setModalOpen(true)}
          sx={{ mr: 3 }}
          disabled={cohortDetails.status === "published"}
        >
          {"Add File for evaluation"}
        </Button> */}
        {/* <Button
          variant="outlined"
          onClick={runEvaluations}
          disabled={isRunning || cohortDetails.status === "published"}
        >
          {isRunning
            ? "Running Evaluations"
            : totalCount <= 0
              ? "Run Evaluations"
              : "Re-Run Evaluations"}
        </Button> */}

        {/* {totalCount <= 0 && (
          <FormControl
            sx={{
              mr: 3,
              ml: 3,
              minWidth: 160,
              "& .MuiOutlinedInput-root": {
                borderColor: "rgba(25, 118, 210, 0.5)",
                borderRadius: "4px",
              },
              "& .MuiInputLabel-root": {
                color: "#1976d2",
              },
              "& .MuiSelect-outlined": {
                color: "#1976d2",
              },
            }}
            size="small"
          >
            <InputLabel id="demo-select-small-label">SUBMISSIONS</InputLabel>
            <Select
              labelId="demo-select-small-label"
              id="demo-select-small"
              value={numberOfSubmissions}
              label="Submissions"
              onChange={handleChange}
              disabled={selectedIDs.length > 0}
            >
              {Array.from({ length: 20 }, (_, index) => {
                return <MenuItem value={index + 1}>{index + 1}</MenuItem>;
              })}
            </Select>
          </FormControl>
        )}
        {totalCount > 0 && (
          <>
            <FormControl
              sx={{
                mr: 3,
                ml: 3,
                minWidth: 160,
                "& .MuiOutlinedInput-root": {
                  borderColor: "rgba(25, 118, 210, 0.5)",
                  borderRadius: "4px",
                },
                "& .MuiInputLabel-root": {
                  color: "#1976d2",
                },
                "& .MuiSelect-outlined": {
                  color: "#1976d2",
                },
              }}
              size="small"
              disabled={selectedIDs.length > 0}
            >
              <InputLabel id="demo-select-small-label">SUBMISSIONS</InputLabel>
              <Select
                labelId="demo-select-small-label"
                id="demo-select-small"
                value={numberOfSubmissions}
                label="Submissions"
                onChange={handleChange}
              >
                {Array.from({ length: 20 - totalCount + 1 }, (_, index) => {
                  return (
                    <MenuItem value={index + totalCount}>
                      {index + totalCount}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {numberOfSubmissions !== totalCount && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setIsRowSelectable(true);
                  setNumberOfSubmissions(totalCount);
                }}
                sx={{ p: 0 }}
              >
                <CloseIcon />
              </Button>
            )}
          </>
        )} */}
      </Box>
      <Box mt={4}>
        <DataGrid
          rows={evaluations}
          isRowSelectable={() => isRowSelectable_}
          columns={columns}
          getRowId={(row) => {
            return row._id;
          }}
          paginationMode="server"
          pagination
          paginationModel={{
            page: page,
            pageSize: rowsPerPage,
          }}
          rowCount={totalCount}
          sortingMode="server"
          onPaginationModelChange={(newPaginationModel) => {
            setPage(newPaginationModel.page);
            setRowsPerPage(newPaginationModel.pageSize);
          }}
          checkboxSelection={true}
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => {
            setSelectedIDs(newSelection);
          }}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          rowHeight={70}
          loading={loading}
          pageSizeOptions={[5, 10, 25, 50]}
          // slots={{
          //   columnMenu: CustomColumnMenu,
          // }}
          onFilterModelChange={handleFilterChange}
        />
      </Box>
      <Toaster
        message={toastMessage}
        severity={isRunning ? "info" : "error"}
        open={toastOpen}
        onClose={() => setToastOpen(false)}
      ></Toaster>
      {/* <Snackbar open={toastOpen} autoHideDuration={6000} onClose={handleToastClose}>
        <Alert onClose={handleToastClose} severity={isRunning ? "info" : "error"} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar> */}

      {/* <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          p={4}
          sx={{
            width: 400,
            margin: "auto",
            marginTop: "10%",
            backgroundColor: "white",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            Upload Answer file
          </Typography>
          <TextField type="file" fullWidth onChange={handleFileUpload} />
          <Button
            variant="contained"
            color="primary"
            onClick={handleFormSubmit}
            fullWidth
            disabled={formLoading || !file}
            sx={{ mt: 2 }}
          >
            {formLoading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Box>
      </Modal> */}
    </Box>
  );
}

export default DashboardEvaluations;
